import type { EquipmentItem } from '@sb/integrations/types';
import type { Step as RcStep } from '@sb/remote-control/types';

import { type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

class RunSkillRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.RunLearnedModel', 'RunLearnedModel');
  }

  protected validate({ args, ctx }: CanonicalValidateArgs): void {
    type RSArgs = Extract<
      RcStep.Arguments,
      { argumentKind: 'RunLearnedModel' }
    >;
    const a = args as RSArgs;
    this.checkModelPath(a);
    this.checkGpuTaskRunnerProvided(a);
    const equipmentConfig = this.findEquipment(ctx, a.gpuTaskRunnerId!);
    this.checkEquipmentExists(equipmentConfig, a);
  }

  /**
   * Ensure modelPath is provided.
   * Throws ActionRequiredError if missing.
   *
   * @throws {ActionRequiredError}
   */
  private checkModelPath(
    args: Extract<RcStep.Arguments, { argumentKind: 'RunLearnedModel' }>,
  ): void {
    if (!args.modelPath) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Model path not specified',
        fieldId: 'modelPath',
      });
    }
  }

  /**
   * Ensure gpuTaskRunnerId is provided.
   * Throws ActionRequiredError if missing.
   *
   * @throws {ActionRequiredError}
   */
  private checkGpuTaskRunnerProvided(
    args: Extract<RcStep.Arguments, { argumentKind: 'RunLearnedModel' }>,
  ): void {
    if (args.gpuTaskRunnerId == null) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'No GPU Task runner specified',
        fieldId: 'gpuTaskRunnerId',
      });
    }
  }

  private checkEquipmentExists(
    equipmentConfig: EquipmentItem | undefined,
    args: Extract<RcStep.Arguments, { argumentKind: 'RunLearnedModel' }>,
  ): void {
    /**
     * Ensure equipment exists in context.
     * Throws ActionRequiredError if not found.
     *
     * @throws {ActionRequiredError}
     */
    if (!equipmentConfig) {
      throw new ActionRequiredError({
        kind: 'missingEquipment',
        message: `Unable to find GPU Task Runner with id ${args.gpuTaskRunnerId} in equipment manager.`,
        fieldId: 'gpuTaskRunnerId',
      });
    }
  }
}

export const RunSkillValidation: ValidationRule =
  new RunSkillRule().toValidationRule();
